/**
 * 1対1に「事故対応」の指定を付ける画面（issue #242・決定27・docs/01 7-1）。
 *
 * 1対1（AI先輩との1対1を含む）は、部屋の一覧には出しません。管理者は1対1の名前や設定に
 * 触れないためです（決定27）。ただし「事故対応」の指定は、どの種類の部屋にも付けられる印なので、
 * 事故のあとで**その方の1対1を残す**と決めたときのために、この画面を用意しています。
 *
 * この画面でできること（1つだけ・docs/06 原則3）:
 *  - 方を1人選び、その方の1対1に「事故対応」の指定を付ける／外す
 *
 * 決めごと:
 *  - 使えるのは全社を受け持つ方（本部管理者）だけ。できる・できないを決めるのはサーバの門番です
 *  - 出すのはお相手のお名前・最後の投稿の日時・指定の有無だけ。**やりとりの中身は出しません**
 *    （docs/01 15-3「1対1の本文を読み出す道がない」）
 *  - 付ける・外すの前には確認の窓をはさみ、終わったら結果を出す（docs/06 決定22・原則1・2・4）
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, messageForStatus } from '../api/client.js';
import { useApi } from '../api/context.js';
import type {
  DirectChannelForEvidence,
  DirectChannelList,
  MemberPage,
  MemberRow,
  RetentionChangePreview,
  RetentionPurgeOutcome,
} from '../api/types.js';
import {
  afterEvidenceRemoved,
  COUNT_CHANGED_NEXT_STEP,
  countChangedSinceConfirm,
  expiringReversibility,
  expiringSentence,
  losesMessages,
  purgeNextStep,
} from '../channels/retention-change.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { DataTable } from '../components/ui/DataTable.js';
import { FormCard } from '../components/ui/FormCard.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { withReason } from '../format/sentence.js';
import { nextStepAfter } from '../api/load-failure.js';
import { formatTableDateTime } from '../format/datetime.js';
import { ROLE_REQUIREMENTS, useRoleWording } from '../auth/role-wording.js';

/** 探した方の一覧に出す人数（多いときは、お名前をもう少し詳しく入れていただく）。 */
const SEARCH_LIMIT = 20;

interface Banner {
  tone: 'success' | 'error';
  message: string;
  nextStep?: string;
}

export function DirectEvidencePage(): ReactNode {
  const api = useApi();
  // 頼み先に出す役職の呼び名（その会社の呼び名・issue #480）
  const wording = useRoleWording();

  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [found, setFound] = useState<MemberPage | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);

  const [person, setPerson] = useState<MemberRow | null>(null);
  const [rooms, setRooms] = useState<DirectChannelForEvidence[] | null>(null);
  const [roomsError, setRoomsError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [confirming, setConfirming] = useState<DirectChannelForEvidence | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Banner | null>(null);
  /** 指定を外す前に数えた「いま消える件数」（issue #410） */
  const [preview, setPreview] = useState<RetentionChangePreview | null>(null);
  /** 探せなかったときの原因（権限が無いのか、通信などなのかで次の一手を変える・#286 L-26） */
  const [searchFailure, setSearchFailure] = useState<unknown>(null);

  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  async function search(): Promise<void> {
    const q = query.trim();
    if (q === '') {
      setSearchError('お名前を入力してください。');
      return;
    }
    setSearching(true);
    setSearchError(null);
    setFound(null);
    try {
      setFound(await api.get<MemberPage>('/admin/members', { query: { q, limit: SEARCH_LIMIT } }));
    } catch (caught: unknown) {
      setSearchFailure(caught);
      setSearchError(toMessage(caught));
    } finally {
      setSearching(false);
    }
  }

  // 方を選んだら、その方の1対1を読む（指定を変えたあとも読み直す）
  useEffect(() => {
    if (!person) return;
    let cancelled = false;
    setRooms(null);
    setRoomsError(null);
    api
      .get<DirectChannelList>('/admin/channels/direct', { query: { user_id: person.id } })
      .then((loaded) => {
        if (!cancelled) setRooms(loaded.channels);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setRoomsError(toMessage(caught));
      });
    return () => {
      cancelled = true;
    };
  }, [api, person, reloadKey]);

  async function saveEvidence(): Promise<void> {
    const room = confirming;
    if (!room || !person) return;
    const next = !room.is_evidence;
    const label = roomLabel(person, room);
    setBusy(true);
    setResult(null);
    try {
      const saved = await api.patch<{ retention_purge?: RetentionPurgeOutcome }>(
        `/channels/${room.id}`,
        {
          body: next
            ? { is_evidence: next }
            : { is_evidence: next, confirmed_expired_messages: preview?.message_count ?? 0 },
        },
      );
      const later = purgeNextStep(saved?.retention_purge);
      setResult({
        tone: 'success',
        message: next
          ? `${label}に「事故対応」の指定を付けました。保存期間が来ても自動では消えません。`
          : `${label}の「事故対応」の指定を外しました。` +
            afterEvidenceRemoved(preview, saved?.retention_purge),
        ...(later !== undefined ? { nextStep: later } : {}),
      });
      reload();
    } catch (caught: unknown) {
      setResult({
        tone: 'error',
        message: withReason(
          `${label}の「事故対応」の指定を変えられませんでした`,
          toMessage(caught),
        ),
        // 権限が無くて断られたときだけ、その操作ができる役職の方へのご依頼を案内する（通信の失敗などでは時間をおく・#286 L-26）
        nextStep: countChangedSinceConfirm(caught)
          ? COUNT_CHANGED_NEXT_STEP
          : isForbidden(caught)
            ? // 断った理由（…の方だけです）はサーバの文に入っている。頼み先はその会社の呼び名で（#480）
              wording.ask(ROLE_REQUIREMENTS.orgRetention)
            : nextStepAfter(caught, '少し時間をおいて、もう一度お試しください。'),
      });
      if (countChangedSinceConfirm(caught)) reload();
    } finally {
      setConfirming(null);
      setBusy(false);
    }
  }

  /**
   * 指定を外す前に「いま消える件数」をサーバで数えてから窓を開く（issue #410・決定22）。
   * 付けるときは何も消えないので、そのまま窓を開く。
   */
  async function openConfirm(room: DirectChannelForEvidence): Promise<void> {
    setPreview(null);
    if (!room.is_evidence) {
      setConfirming(room);
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      setPreview(
        await api.post<RetentionChangePreview>(`/channels/${room.id}/retention/preview`, {
          body: { is_evidence: false },
        }),
      );
      setConfirming(room);
    } catch (caught: unknown) {
      setResult({
        tone: 'error',
        message: withReason(`消える件数を確かめられませんでした`, toMessage(caught)),
        nextStep: nextStepAfter(caught, '少し時間をおいて、もう一度お試しください。'),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <article>
      <PageHeader
        title="1対1の「事故対応」の指定"
        description="事故などで記録を残す必要があるとき、その方の1対1に「事故対応」の指定を付けます。付けた1対1は、保存期間が来ても自動では消えません。やりとりの中身は、この画面からは見られません。"
        actions={
          <Link className="ui-button ui-button-secondary" to="/rooms">
            トークの一覧に戻る
          </Link>
        }
      />

      <FormCard
        title="1対1を調べるメンバーを探す"
        submitLabel="この名前で探す"
        submitBusyLabel="探しています…"
        busy={searching}
        onSubmit={() => void search()}
        footer={
          searchError ? (
            <ResultBanner
              tone="error"
              message={withReason(`探せませんでした`, searchError)}
              nextStep={
                isForbidden(searchFailure)
                  ? `メンバーを見る権限が要ります。${wording.ask(ROLE_REQUIREMENTS.memberManage)}`
                  : nextStepAfter(searchFailure, '少し時間をおいて、もう一度お試しください。')
              }
            />
          ) : undefined
        }
      >
        <label className="ui-field" htmlFor="direct-person-q">
          <span>お名前・スタッフID</span>
          <input
            id="direct-person-q"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
      </FormCard>

      {found ? (
        found.items.length === 0 ? (
          <EmptyBlock label="この名前の方は見つかりませんでした。お名前を変えてお試しください。" />
        ) : (
          <>
            {found.nextCursor ? (
              <p className="admin-note">
                {SEARCH_LIMIT}名より多く見つかりました。お名前をもう少し詳しく入れてください。
              </p>
            ) : null}
            <DataTable label="見つかった方">
              <thead>
                <tr>
                  <th scope="col">お名前</th>
                  <th scope="col">拠点</th>
                  <th scope="col">
                    <span className="admin-visually-hidden">選ぶ</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {found.items.map((member) => (
                  <tr key={member.id}>
                    <th scope="row">{member.display_name}</th>
                    <td className="ui-cell-soft">{member.site_name ?? '―'}</td>
                    <td>
                      <Button
                        variant={person?.id === member.id ? 'primary' : 'secondary'}
                        compact
                        aria-pressed={person?.id === member.id}
                        onClick={() => {
                          setPerson(member);
                          setResult(null);
                        }}
                      >
                        この方の1対1を選ぶ
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </>
        )
      ) : null}

      {person ? (
        <Card
          title={`${person.display_name}さんの1対1`}
          description="お相手と、最後にやりとりした日時だけを出しています。"
        >
          {result ? (
            <ResultBanner
              tone={result.tone}
              message={result.message}
              {...(result.nextStep !== undefined ? { nextStep: result.nextStep } : {})}
            />
          ) : null}
          {roomsError ? (
            <ErrorBlock message={roomsError} onRetry={reload} />
          ) : rooms === null ? (
            <LoadingBlock label="1対1を読み込んでいます…" />
          ) : rooms.length === 0 ? (
            <EmptyBlock label="この方の1対1はありません。" />
          ) : (
            <DataTable label={`${person.display_name}さんの1対1の一覧`}>
              <thead>
                <tr>
                  <th scope="col">お相手</th>
                  <th scope="col">
                    最後の
                    <wbr />
                    メッセージ
                  </th>
                  <th scope="col">事故対応</th>
                  <th scope="col">
                    <span className="admin-visually-hidden">操作</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rooms.map((room) => (
                  <tr key={room.id}>
                    <th scope="row">{partnerLabel(room)}</th>
                    <td className="ui-cell-date">{formatTableDateTime(room.last_message_at)}</td>
                    <td>
                      {room.is_evidence ? (
                        <span className="admin-badge">指定あり</span>
                      ) : (
                        <span className="admin-mark-no">なし</span>
                      )}
                    </td>
                    <td>
                      <Button
                        variant={room.is_evidence ? 'danger' : 'secondary'}
                        compact
                        busy={busy && confirming?.id === room.id}
                        disabled={busy}
                        onClick={() => void openConfirm(room)}
                      >
                        {room.is_evidence ? '指定を外す' : '指定を付ける'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          )}
        </Card>
      ) : null}

      <ConfirmDialog
        open={
          confirming !== null && person !== null && (!confirming.is_evidence || preview !== null)
        }
        title={
          confirming?.is_evidence ? '「事故対応」の指定を外します' : '「事故対応」の指定を付けます'
        }
        message={
          confirming && person
            ? confirming.is_evidence
              ? `${roomLabel(person, confirming)}の「事故対応」の指定を外します。${preview ? expiringSentence(preview) : ''}`
              : `${roomLabel(person, confirming)}は、保存期間が来ても自動では消えなくなります。`
            : ''
        }
        reversibility={
          confirming?.is_evidence && preview
            ? expiringReversibility(preview)
            : 'この変更は操作履歴に残ります。'
        }
        reversible={!(confirming?.is_evidence && losesMessages(preview))}
        confirmLabel={confirming?.is_evidence ? '指定を外す' : '指定を付ける'}
        // 付けるのは記録を守る（先へ進める）操作なので主役の色、外すのは赤（docs/06 4-7・#448 L-12）
        tone={confirming?.is_evidence ? 'danger' : 'primary'}
        busy={busy}
        onConfirm={() => void saveEvidence()}
        onCancel={() => setConfirming(null)}
      />
    </article>
  );
}

/** お相手の言い方（退出された方は、お名前が分からないことをそのまま伝える）。 */
function partnerLabel(room: DirectChannelForEvidence): string {
  if (room.partner_display_name === null) return '（退出された方）';
  return room.partner_is_assistant ? room.partner_display_name : `${room.partner_display_name}さん`;
}

/** 確認の窓・結果のお知らせで使う、1対1の言い方（「AさんとBさんの1対1」）。 */
function roomLabel(person: MemberRow, room: DirectChannelForEvidence): string {
  return `${person.display_name}さんと${partnerLabel(room)}の1対1`;
}

/** 失敗の理由を、運用される方が読める言葉にする。 */
function toMessage(caught: unknown): string {
  return caught instanceof ApiError ? caught.message : messageForStatus(0);
}

/** 権限が無くて断られたか（403）。 */
function isForbidden(caught: unknown): boolean {
  return caught instanceof ApiError && caught.status === 403;
}
